import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFarmsListRequest } from '../farms/farms.actions';
import { createCostType, deleteCostType, getCostTypes, updateCostType } from './costTypes.actions';
import { successHandler } from '../../../core/helpers/utils/sagaUtils';
import { Forms } from '../../form/form.selector';

export function* getCostTypesSaga(action: ReturnType<typeof getCostTypes.started>) {
  yield put(showLoader(LoadersNames.getCostTypes));
  try {
    const canReadCostTypes = yield call(checkPermissions, {
      resource: Resource.FEED_SETTINGS_LOTADDITIONALCOSTTYPES,
      action: Action.READ,
    });
    if (canReadCostTypes) {
      const result = yield call(api.costTypes.getCostTypes);
      yield put(
        getCostTypes.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getCostTypes));
}

export function* createCostTypeSaga(action: ReturnType<typeof createCostType.started>) {
  yield put(showLoader(LoadersNames.createCostType));
  try {
    const { payload, meta } = action;
    const { formName } = payload;
    const data = yield select(getFormValues(Forms.costTypesCreationForm));
    const result = yield call(api.costTypes.createCostType, data, formName);
    yield put(
      createCostType.done({
        params: payload,
        result,
      }),
    );
    yield put(notificationSuccess('lots:cost_type_create_success'));
    successHandler(meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.createCostType));
}

export function* updateCostTypeSaga(action: ReturnType<typeof updateCostType.started>) {
  yield put(showLoader(LoadersNames.createCostType));
  try {
    const { payload, meta } = action;
    const { id, formName } = payload;
    const data = yield select(getFormValues(Forms.costTypesCreationForm));
    const result = yield call(api.costTypes.updateCostType, id, data, formName);
    yield put(
      updateCostType.done({
        params: payload,
        result,
      }),
    );
    yield put(notificationSuccess('lots:cost_type_edit_success'));
    successHandler(meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.createCostType));
}

export function* deleteCostTypeSaga(action: ReturnType<typeof deleteCostType.started>) {
  try {
    const { id, formName } = action.payload;
    yield call(api.costTypes.deleteCostType, id, formName);
    yield put(
      deleteCostType.done({
        params: action.payload,
        result: id,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('lots:cost_type_delete_success'));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* watchCostTypesSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, getCostTypesSaga),
    yield takeLatest(getCostTypes.started, getCostTypesSaga),
    yield takeLatest(createCostType.started, createCostTypeSaga),
    yield takeLatest(updateCostType.started, updateCostTypeSaga),
    yield takeLatest(deleteCostType.started, deleteCostTypeSaga),
  ]);
}
